import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import Face from '@mui/icons-material/Face';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import CheckCircle from '@mui/icons-material/CheckCircle';
import axios from './utils/axiosConfig';
import ErrorIcon from '@mui/icons-material/Error';
import Refresh from '@mui/icons-material/Refresh';
import Home from '@mui/icons-material/Home';

const FaceCheckIn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // 從 URL 參數獲取活動 ID
  const searchParams = new URLSearchParams(location.search);
  const activityId = searchParams.get('aid');
  
  // 狀態管理
  const [currentStep, setCurrentStep] = useState('init'); // init, monitoring, processing, success, error, alreadyCheckedIn
  const [cameraStream, setCameraStream] = useState(null);
  const [error, setError] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [checkInResult, setCheckInResult] = useState(null);
  const [activityInfo, setActivityInfo] = useState(null);
  const [serviceAvailable, setServiceAvailable] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [lastCheckInTime, setLastCheckInTime] = useState(0);
  const [overlayMessage, setOverlayMessage] = useState('');
  const [showOverlay, setShowOverlay] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkedInList, setCheckedInList] = useState([]);
  const [totalCheckedIn, setTotalCheckedIn] = useState(0);
  const [debugMode, setDebugMode] = useState(false);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' for front, 'environment' for back
  const [detectionStats, setDetectionStats] = useState({
    totalAttempts: 0,
    successfulDetections: 0,
    recognitionAttempts: 0,
    successfulRecognitions: 0
  });
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const monitoringIntervalRef = useRef(null);
  const recordsRefreshRef = useRef(null);

  // 語音播放功能
  const playVoiceMessage = useCallback((message) => {
    try {
      // 使用 Web Speech API 進行語音播放
      if ('speechSynthesis' in window) {
        // 停止當前正在播放的語音
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(message);
        utterance.lang = 'zh-TW'; // 繁體中文
        utterance.rate = 0.8; // 語速稍慢
        utterance.volume = 0.8; // 音量
        utterance.pitch = 1.0; // 音調

        window.speechSynthesis.speak(utterance);
      }
    } catch (error) {
      // 語音播放失敗時靜默處理
    }
  }, []);

  // 顯示 overlay 訊息
  const showOverlayMessage = useCallback((message, duration = 3000) => {
    setOverlayMessage(message);
    setShowOverlay(true);
    
    // 播放語音
    playVoiceMessage(message);
    
    setTimeout(() => {
      setShowOverlay(false);
      setOverlayMessage('');
    }, duration);
  }, [playVoiceMessage]);

  // 獲取活動簽到記錄
  const fetchCheckedInRecords = useCallback(async () => {
    if (!activityId) return;

    try {
      const response = await axios.get(`/checkin/face/activity/${activityId}/records?limit=20`);
      const result = response.data;

      if (result.success) {
        setCheckedInList(result.data.records);
        setTotalCheckedIn(result.data.total);
      }
    } catch (error) {
      // 獲取簽到記錄失敗時靜默處理
    }
  }, [activityId]);

  // 持續監控模式的人臉識別
  const captureAndRecognize = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || !activityId || isProcessing) return;

    try {
      setIsProcessing(true);

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // 智能計算最佳尺寸 - 根據原始解析度和比例
      const originalWidth = video.videoWidth;
      const originalHeight = video.videoHeight;
      const aspectRatio = originalWidth / originalHeight;
      
      let targetWidth, targetHeight;
      
      // 根據原始解析度決定目標解析度
      if (originalWidth >= 1280 || originalHeight >= 720) {
        // 高解析度：保持較高品質
        if (aspectRatio > 1) {  // 橫向 (16:9 等)
          targetWidth = 1024;
          targetHeight = Math.round(1024 / aspectRatio);
        } else {  // 直向 (9:16 等)
          targetHeight = 1024;
          targetWidth = Math.round(1024 * aspectRatio);
        }
      } else {
        // 中低解析度：適度提升
        if (aspectRatio > 1) {  // 橫向
          targetWidth = 800;
          targetHeight = Math.round(800 / aspectRatio);
        } else {  // 直向
          targetHeight = 800;
          targetWidth = Math.round(800 * aspectRatio);
        }
      }
      
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      // 只有前置相機才需要鏡像翻轉
      if (facingMode === 'user') {
        context.save();
        context.scale(-1, 1);
        
        // 繪製完整畫面（保持原始比例）
        context.drawImage(
          video, 
          0, 0, originalWidth, originalHeight,     // 源區域（完整畫面）
          -targetWidth, 0, targetWidth, targetHeight  // 目標區域
        );
        context.restore();
      } else {
        // 後置相機不需要翻轉
        context.drawImage(
          video, 
          0, 0, originalWidth, originalHeight,     // 源區域（完整畫面）
          0, 0, targetWidth, targetHeight  // 目標區域
        );
      }

      // 轉換為 base64 - 提高圖片品質
      const photoData = canvas.toDataURL('image/jpeg', 0.85);

      // 調試：顯示實際捕獲的圖片
      if (debugMode || window.DEBUG_FACE_CAPTURE) {
        // 創建一個 img 元素來顯示捕獲的圖片
        const debugImg = document.createElement('img');
        debugImg.src = photoData;
        debugImg.style.cssText = 'position:fixed;top:10px;right:10px;width:300px;height:300px;border:3px solid #ff4444;z-index:9999;background:white;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.3);';
        debugImg.title = '調試：實際捕獲的圖片 - ' + new Date().toLocaleTimeString();

        // 添加調試資訊標籤
        const debugLabel = document.createElement('div');
        debugLabel.style.cssText = 'position:fixed;top:320px;right:10px;background:#ff4444;color:white;padding:8px;border-radius:4px;font-size:12px;z-index:10000;';
        debugLabel.textContent = `調試圖片 ${new Date().toLocaleTimeString()}`;

        document.body.appendChild(debugImg);
        document.body.appendChild(debugLabel);

        // 5秒後移除調試圖片
        setTimeout(() => {
          if (debugImg.parentNode) {
            debugImg.parentNode.removeChild(debugImg);
          }
          if (debugLabel.parentNode) {
            debugLabel.parentNode.removeChild(debugLabel);
          }
        }, 5000);
      }

      // 呼叫人臉簽到 API
      const response = await axios.post('/checkin/face/verify', {
        photo: photoData,
        activityId: activityId
      });

      const result = response.data;

      // 更新統計
      setDetectionStats(prev => ({
        ...prev,
        totalAttempts: prev.totalAttempts + 1,
        recognitionAttempts: prev.recognitionAttempts + 1,
        successfulRecognitions: result.success ? prev.successfulRecognitions + 1 : prev.successfulRecognitions
      }));

      if (result.success) {
        const employeeName = result.data.employee.name;
        const message = `${employeeName} 已簽到完成`;

        // 顯示 overlay 訊息並播放語音
        showOverlayMessage(message, 4000);

        setLastCheckInTime(Date.now());

        // 刷新簽到記錄
        setTimeout(() => {
          fetchCheckedInRecords();
        }, 1000);
      } else if (result.code === 'DUPLICATE_CHECKIN' || (result.error && result.error.includes('已簽到'))) {
        const employeeName = result.employee?.name || '該員工';
        const message = `${employeeName} 已簽到過了`;

        // 顯示 overlay 訊息並播放語音
        showOverlayMessage(message, 3000);

        setLastCheckInTime(Date.now());

        // 刷新簽到記錄以顯示現有記錄
        setTimeout(() => {
          fetchCheckedInRecords();
        }, 1000);
      }

    } catch (err) {
    } finally {
      setIsProcessing(false);
    }
  }, [activityId, isProcessing, showOverlayMessage]);

  // 開始持續監控
  const startMonitoring = useCallback(() => {
    if (monitoringIntervalRef.current) return;
    
    setIsMonitoring(true);
    setCurrentStep('monitoring');
    
    // 初始載入簽到記錄
    fetchCheckedInRecords();
    
    // 每 2 秒檢測一次人臉
    monitoringIntervalRef.current = setInterval(() => {
      // 避免太頻繁檢測，至少間隔 3 秒
      if (Date.now() - lastCheckInTime > 3000) {
        captureAndRecognize();
      }
    }, 2000);
    
    // 每 10 秒刷新一次簽到記錄
    recordsRefreshRef.current = setInterval(() => {
      fetchCheckedInRecords();
    }, 10000);
  }, [captureAndRecognize, lastCheckInTime, fetchCheckedInRecords]);

  // 停止監控
  const stopMonitoring = useCallback(() => {
    if (monitoringIntervalRef.current) {
      clearInterval(monitoringIntervalRef.current);
      monitoringIntervalRef.current = null;
    }
    if (recordsRefreshRef.current) {
      clearInterval(recordsRefreshRef.current);
      recordsRefreshRef.current = null;
    }
    setIsMonitoring(false);
  }, []);

  // 設置相機串流到 video 元素
  React.useEffect(() => {
    if (cameraStream && videoRef.current) {
      const video = videoRef.current;

      // 設置 srcObject
      video.srcObject = cameraStream;

      // 設置事件監聽器
      const handleLoadedMetadata = () => {
        if (video.videoWidth === 0 || video.videoHeight === 0) {
          setError('相機畫面無法正常顯示，請檢查相機設備');
          return;
        }

        // 自動播放
        video.play().then(() => {
          // 2秒後開始監控
          setTimeout(() => {
            if (currentStep === 'monitoring' || currentStep === 'camera') {
              startMonitoring();
            }
          }, 2000);
        }).catch(err => {
          setError('無法開始影片播放，請重試');
        });
      };
      
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      
      // 立即嘗試播放（如果數據已準備好）
      if (video.readyState >= 2) {
        handleLoadedMetadata();
      }
      
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [cameraStream, currentStep, startMonitoring]);

  // 清理監控間隔
  React.useEffect(() => {
    return () => {
      stopMonitoring();
    };
  }, [stopMonitoring]);

  // 檢查人臉識別服務可用性和初始化相機列表
  useEffect(() => {
    const checkServiceAvailability = async () => {
      try {
        const response = await axios.get('/checkin/face/availability');
        const result = response.data;
        
        if (result.success && result.data.available) {
          setServiceAvailable(true);
          // 初始化相機列表
          await getAvailableCameras();
        } else {
          setServiceAvailable(false);
          setError(result.data?.message || '人臉識別服務暫時無法使用');
        }
      } catch (err) {
        setServiceAvailable(false);
        setError('無法連接到人臉識別服務');
      }
    };

    if (activityId) {
      checkServiceAvailability();
    } else {
      setError('缺少活動參數，請掃描正確的 QR Code');
    }
  }, [activityId]);

  // 獲取可用相機設備
  const getAvailableCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(videoDevices);
      
      // 如果沒有選擇的相機，預設選擇第一個前置相機
      if (!selectedCameraId && videoDevices.length > 0) {
        const frontCamera = videoDevices.find(device => 
          device.label.toLowerCase().includes('front') || 
          device.label.toLowerCase().includes('user')
        ) || videoDevices[0];
        setSelectedCameraId(frontCamera.deviceId);
      }
    } catch (error) {
      // 獲取相機設備失敗時靜默處理
    }
  }, [selectedCameraId]);

  // 初始化相機
  const initializeCamera = useCallback(async (cameraId = null) => {
    try {
      setError(null);
      
      // 檢查瀏覽器支援
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('您的瀏覽器不支援相機功能');
      }

      // 停止現有串流
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }

      // 先進入監控步驟
      setCurrentStep('monitoring');

      // 短暫等待 DOM 更新
      await new Promise(resolve => setTimeout(resolve, 100));

      // 使用與測試頁面相同的最簡單設定
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false
      });

      setCameraStream(stream);

    } catch (err) {
      // 設定錯誤訊息
      if (err.name === 'NotAllowedError') {
        setError('❌ 相機權限被拒絕！請點擊瀏覽器地址欄的相機圖示，選擇「允許」，然後重新整理頁面。');
      } else if (err.name === 'NotFoundError') {
        setError('❌ 找不到相機設備！請確認：1. 相機已連接 2. 其他程式沒有佔用相機 3. 重新整理頁面重試');
      } else if (err.name === 'NotReadableError') {
        setError('❌ 相機被佔用！請關閉其他使用相機的程式（如視訊會議、其他瀏覽器頁面），然後重新整理頁面。');
      } else {
        setError(`❌ 相機啟動失敗: ${err.message}。請重新整理頁面重試。`);
      }
      setCurrentStep('init');
    }
  }, [cameraStream]);

  // 切換前後鏡頭
  const toggleCamera = useCallback(async () => {
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
    
    // 如果有多個相機，切換到對應的相機
    if (availableCameras.length > 1) {
      const targetCamera = availableCameras.find(camera => {
        const label = camera.label.toLowerCase();
        return newFacingMode === 'environment' ?
          (label.includes('back') || label.includes('rear')) :
          (label.includes('front') || label.includes('user'));
      });

      if (targetCamera) {
        setSelectedCameraId(targetCamera.deviceId);
        await initializeCamera(targetCamera.deviceId);
        return;
      }
    }

    // 否則使用 facingMode 切換
    await initializeCamera();
  }, [facingMode, availableCameras, initializeCamera]);

  // 停止相機
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  }, [cameraStream]);


  // 重試
  const handleRetry = useCallback(() => {
    setError(null);
    setProcessingProgress(0);
    setCheckInResult(null);
    setOverlayMessage('');
    setShowOverlay(false);
    if (cameraStream) {
      startMonitoring();
    } else {
      setCurrentStep('init');
    }
  }, [cameraStream, startMonitoring]);

  // 返回首頁
  const handleGoHome = useCallback(() => {
    stopMonitoring();
    stopCamera();
    navigate('/');
  }, [stopMonitoring, stopCamera, navigate]);

  // 顯示幫助對話框
  const showHelpDialog = () => {
    setIsDialogOpen(true);
  };

  // 渲染不同步驟的內容
  const renderStepContent = () => {
    switch (currentStep) {
      case 'init':
        return (
          <Card>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <Face sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                人臉簽到
              </Typography>
              <Typography variant="body1" color="textSecondary" mb={3}>
                請準備進行人臉識別簽到
              </Typography>
              
              {!serviceAvailable && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box mb={3} p={2} sx={{ backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #2196f3' }}>
                <Typography variant="body1" color="primary" textAlign="center" mb={2} sx={{ fontWeight: 'bold' }}>
                  📝 使用提示
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>✓</span> 確保光線充足，避免逆光或陰影
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>✓</span> 面向相機正面，保持面部清晰可見
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>✓</span> 一次只能有一人在畫面中
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>✓</span> 請勿佩戴口罩、帽子或太陽鏡
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'info.main' }}>
                    <span>📱</span> 支援前後鏡頭切換，手機用戶建議使用前鏡頭
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                    <span>🔍</span> 可開啟調試模式查看擷取圖片
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<PhotoCamera />}
                onClick={() => initializeCamera()}
                disabled={!serviceAvailable || !activityId}
                sx={{ 
                  fontSize: '18px', 
                  py: 2, 
                  px: 4,
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}
              >
                🚀 開始人臉簽到
              </Button>

              <Box mt={2} display="flex" gap={2} justifyContent="center">
                <Button
                  variant="text"
                  onClick={showHelpDialog}
                >
                  需要幫助？
                </Button>
                <Button
                  variant="text"
                  color="info"
                  onClick={async () => {
                    try {
                      const devices = await navigator.mediaDevices.enumerateDevices();
                      const videoDevices = devices.filter(d => d.kind === 'videoinput');
                      alert(`找到 ${videoDevices.length} 個相機設備`);
                    } catch (err) {
                      alert('無法獲取相機設備列表：' + err.message);
                    }
                  }}
                >
                  🔍 檢測相機
                </Button>
              </Box>
            </CardContent>
          </Card>
        );

      case 'monitoring':
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" textAlign="center" gutterBottom>
                人臉識別簽到系統 - 持續監控中
              </Typography>
              
              {/* 相機預覽 with Overlay */}
              <Box position="relative" textAlign="center" mb={2}>
                {!cameraStream ? (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: '400px',
                      aspectRatio: '3/4',
                      borderRadius: '12px',
                      background: 'linear-gradient(45deg, #f0f0f0, #e0e0e0)',
                      border: '3px solid #ff9800',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#666',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      padding: 2
                    }}
                  >
                    <i className="fas fa-camera fa-3x mb-3" style={{color: '#ff9800'}}></i>
                    <div>正在啟動相機...</div>
                    <div style={{fontSize: '14px', marginTop: '10px', color: '#999'}}>
                      請稍候，正在請求相機權限
                    </div>
                  </Box>
                ) : (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      maxWidth: '400px',  // 減小寬度適應直立模式
                      height: 'auto',
                      aspectRatio: '3/4',  // 調整為直立比例 (3:4)
                      borderRadius: '12px',
                      background: '#000',
                      transform: facingMode === 'user' ? 'scaleX(-1)' : 'none', // 前置相機才鏡像翻轉
                      objectFit: 'cover',  // 改為 cover 以填滿框架
                      border: '3px solid #2196f3'
                    }}
                    onError={(e) => {
                      setError('影片播放出現錯誤，請重新整理頁面');
                    }}
                  />
                )}
                <canvas
                  ref={canvasRef}
                  style={{ display: 'none' }}
                />

                {/* Overlay 訊息顯示 */}
                {showOverlay && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(76, 175, 80, 0.95)',
                      color: 'white',
                      padding: '40px 60px',
                      borderRadius: '20px',
                      fontSize: '48px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      border: '4px solid #4caf50',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                      zIndex: 1000,
                      minWidth: '400px',
                      animation: 'fadeInOut 0.5s ease-in-out'
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 80, mb: 2 }} />
                    <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                      {overlayMessage}
                    </Typography>
                  </Box>
                )}
                
                {/* 監控狀態指示 */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    backgroundColor: isProcessing ? 'warning.main' : 'success.main',
                    color: 'white',
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    fontSize: '18px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      animation: isProcessing ? 'pulse 1s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                        '100%': { opacity: 1 }
                      }
                    }}
                  />
                  {isProcessing ? '辨識中' : '監控中'}
                </Box>
                
                {/* 統計信息 */}
                {debugMode && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 70,
                      left: 20,
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      fontSize: '12px'
                    }}
                  >
                    <div>檢測次數: {detectionStats.totalAttempts}</div>
                    <div>識別成功率: {detectionStats.totalAttempts > 0 ? 
                      ((detectionStats.successfulRecognitions / detectionStats.totalAttempts) * 100).toFixed(1) : 0}%</div>
                  </Box>
                )}
                
                {/* 人臉框架指引 - 適應直立模式 */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '70%',
                    height: '45%',  // 適應人臉比例
                    border: '3px solid #4caf50',
                    borderRadius: '12px',
                    pointerEvents: 'none',
                    opacity: showOverlay ? 0 : 0.8,
                    transition: 'opacity 0.3s ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '20%',
                      left: '20%',
                      right: '20%',
                      bottom: '20%',
                      border: '2px dashed #4caf50',
                      borderRadius: '8px',
                      opacity: 0.6
                    },
                    // 加上角落指示
                    '&::after': {
                      content: '"👤"',
                      position: 'absolute',
                      top: '-25px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '20px',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }
                  }}
                />
                
                {/* 人臉識別指引 */}
                {!showOverlay && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 15,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'rgba(76, 175, 80, 0.9)',
                      color: 'white',
                      px: 3,
                      py: 1.5,
                      borderRadius: 3,
                      fontSize: '16px',
                      fontWeight: 'bold',
                      opacity: 0.9,
                      textAlign: 'center',
                      minWidth: '250px'
                    }}
                  >
                    👤 請將人臉置於綠色框架內
                  </Box>
                )}
              </Box>

              {/* 操作按鈕區域 */}
              <Box display="flex" justifyContent="center" gap={2} mb={3} flexWrap="wrap">
                {availableCameras.length > 1 && (
                  <Button
                    variant="outlined"
                    color="info"
                    onClick={toggleCamera}
                    startIcon={<PhotoCamera />}
                    size="large"
                    disabled={isProcessing}
                  >
                    {facingMode === 'user' ? '📱 切換後鏡頭' : '🤳 切換前鏡頭'}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color={debugMode ? "warning" : "info"}
                  onClick={() => setDebugMode(!debugMode)}
                  size="large"
                  sx={{ fontSize: '14px' }}
                >
                  {debugMode ? '🔍 關閉調試' : '🔍 開啟調試'}
                </Button>
              </Box>

              <Typography variant="body1" textAlign="center" color="primary" mb={3} sx={{ fontSize: '18px', fontWeight: 'bold' }}>
                🔍 系統正在持續監控中，請站在相機前方即可自動簽到
              </Typography>

              {/* 使用指引 */}
              <Box mb={3} p={2} sx={{ backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #2196f3' }}>
                <Typography variant="h6" color="primary" textAlign="center" mb={2}>
                  📝 使用指引
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>✓</span> 請將臉部置於藍色方框內
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>✓</span> 保持面部正向相機，面部清晰可見
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>✓</span> 確保光線充足，避免逆光或陰影
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>✓</span> 一次只能有一人在畫面中
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>✓</span> 請勿佩戴口罩、帽子或太陽鏡
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'info.main' }}>
                    <span>📱</span> 手機用戶可使用前鏡頭進行自拍
                  </Typography>
                </Box>
              </Box>

              {/* 已簽到人員列表 */}
              <Box mb={4}>
                <Typography variant="h6" textAlign="center" mb={2}>
                  📋 已簽到人員 ({totalCheckedIn} 人)
                </Typography>
                <Box 
                  sx={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    p: 2
                  }}
                >
                  {checkedInList.length === 0 ? (
                    <Typography textAlign="center" color="textSecondary">
                      暫無簽到記錄
                    </Typography>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={1}>
                      {checkedInList.map((record, index) => (
                        <Box
                          key={record.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            p: 2,
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="h6" color="primary" sx={{ minWidth: '30px' }}>
                              {index + 1}.
                            </Typography>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {record.employee.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {record.employee.store}
                              </Typography>
                            </Box>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="body2" color="success.main" fontWeight="bold">
                              {record.checkIn.method === 'face' ? '人臉識別' : record.checkIn.method}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(record.checkIn.time).toLocaleTimeString('zh-TW')}
                            </Typography>
                            {record.checkIn.confidence && (
                              <Typography variant="caption" display="block" color="info.main">
                                信心度: {(record.checkIn.confidence * 100).toFixed(1)}%
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>

              <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={stopMonitoring}
                  size="large"
                >
                  停止監控
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => initializeCamera()}
                  size="large"
                  disabled={isProcessing}
                >
                  🔄 重新啟動相機
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleGoHome}
                  startIcon={<Home />}
                  size="large"
                >
                  返回首頁
                </Button>
              </Box>
            </CardContent>
          </Card>
        );

      case 'processing':
        return (
          <Card>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <Face sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                正在進行人臉識別...
              </Typography>
              
              <LinearProgress 
                variant="determinate" 
                value={processingProgress} 
                sx={{ mb: 2, height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="textSecondary">
                {processingProgress}% 完成
              </Typography>
              
              <Typography variant="body2" color="textSecondary" mt={2}>
                請稍候，正在驗證您的身份...
              </Typography>
            </CardContent>
          </Card>
        );

      case 'success':
        return (
          <Card>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
              
              {checkInResult && (
                <>
                  <Typography variant="h3" gutterBottom color="success.main" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {checkInResult.employee.name}
                  </Typography>
                  <Typography variant="h4" gutterBottom color="success.main" sx={{ mb: 3 }}>
                    已簽到完成！
                  </Typography>
                  
                  <Box mb={3}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      {checkInResult.employee.store}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" gap={1} justifyContent="center" mb={3}>
                    <Chip 
                      label={`識別信心度: ${(checkInResult.checkIn.confidence * 100).toFixed(1)}%`}
                      color="success"
                      size="medium"
                      sx={{ fontSize: '14px' }}
                    />
                    <Chip 
                      label="人臉識別"
                      color="primary"
                      size="medium"
                      sx={{ fontSize: '14px' }}
                    />
                  </Box>
                  
                  <Typography variant="body1" color="textSecondary" mb={4} sx={{ fontSize: '16px' }}>
                    簽到時間: {new Date(checkInResult.checkIn.time).toLocaleString('zh-TW')}
                  </Typography>
                </>
              )}
              
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleRetry}
                  size="large"
                  sx={{ fontSize: '18px', py: 2, px: 6 }}
                >
                  下一位簽到
                </Button>
                
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleGoHome}
                  startIcon={<Home />}
                  size="medium"
                  sx={{ fontSize: '14px' }}
                >
                  返回首頁
                </Button>
              </Box>
            </CardContent>
          </Card>
        );

      case 'error':
        return (
          <Card>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom color="error.main">
                簽到失敗
              </Typography>
              
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              
              <Typography variant="body2" color="textSecondary" mb={3}>
                請檢查以下事項：
                <br />• 確保您已註冊人臉識別
                <br />• 光線充足且面部清晰
                <br />• 網路連接正常
              </Typography>
              
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRetry}
                  startIcon={<Refresh />}
                  size="large"
                  sx={{ fontSize: '18px', py: 2, px: 6 }}
                >
                  下一位簽到
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={handleGoHome}
                  startIcon={<Home />}
                  size="medium"
                  sx={{ fontSize: '14px' }}
                >
                  返回首頁
                </Button>
              </Box>
            </CardContent>
          </Card>
        );

      case 'alreadyCheckedIn':
        return (
          <Card>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <CheckCircle sx={{ fontSize: 80, color: 'warning.main', mb: 3 }} />
              
              {checkInResult && (
                <>
                  <Typography variant="h3" gutterBottom color="warning.main" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {checkInResult.employee.name}
                  </Typography>
                  <Typography variant="h4" gutterBottom color="warning.main" sx={{ mb: 3 }}>
                    已簽到過了！
                  </Typography>
                  
                  <Box mb={3}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      {checkInResult.employee.store}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" color="textSecondary" mb={4} sx={{ fontSize: '16px' }}>
                    請下一位準備簽到
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  // 組件卸載時清理資源
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  return (
    <>
      {/* 全局 CSS 樣式 */}
      <style>
        {`
          @keyframes fadeInOut {
            0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
            20% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
            80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
            100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          }
          
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" gutterBottom>
            {(process.env.REACT_APP_CLUB_SHORT_NAME || 'TWVN') + ' 人臉簽到系統'}
          </Typography>
          {activityId && (
            <Chip 
              label={`活動 ID: ${activityId}`}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

      {renderStepContent()}

      {/* 幫助對話框 */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>人臉簽到使用說明</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            <strong>使用前準備：</strong>
          </Typography>
          <Typography variant="body2" paragraph>
            1. 確保您已在個人資料頁面註冊人臉識別<br />
            2. 使用現代瀏覽器（Chrome、Firefox、Safari）<br />
            3. 允許瀏覽器使用相機權限
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>拍攝建議：</strong>
          </Typography>
          <Typography variant="body2" paragraph>
            1. 在光線充足的環境下使用<br />
            2. 面向相機，保持正面角度<br />
            3. 確保面部完整出現在藍色圓圈內<br />
            4. 拍攝時請勿佩戴口罩或帽子
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>常見問題：</strong>
          </Typography>
          <Typography variant="body2">
            • 如果識別失敗，請檢查光線和拍攝角度<br />
            • 如果提示未註冊，請先到個人資料頁面註冊人臉<br />
            • 如果相機無法啟動，請檢查瀏覽器權限設定
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>
            我知道了
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
};

export default FaceCheckIn;
